import { appendIf } from '@btime/helpers'
import { path } from 'ramda'
import { useSessionPermission } from '@btime/react-session'
import { useTranslation } from 'react-i18next'
import React from 'react'

import { Col, Grid } from '../../../../../../packages/kiti/components/structure/Grid'
import { useComplementaryOptionsGenerator } from '../../../_helpers/complementaryOptions'
import { useFormState } from '../../../../../../packages/kiti/components/form/form/FormHandler'
import AssetSelect from '../../../../../../containers/assets/select/AssetSelect'
import BondSelect from '../insertServiceOrder/BondSelect'
import BulkForm from '../../../../../../packages/kiti/components/form/form/BulkForm'
import ChecklistSelect from '../../../../../../containers/checklists/select/ChecklistSelect'
import EditableSentence from '../../EditableSentence'
import Icon from '../../../../../../packages/kiti/components/shapes/Icon'
import Line from '../../../../../../packages/kiti/components/helpers/Line'
import Modal from '../../../../../../components/structure/Modal'
import PlaceSelect from '../../../../../../containers/places/select/PlaceSelect'
import Scroll from '../../../../../../packages/kiti/components/helpers/Scroll'
import Separator from '../../../../../../packages/kiti/components/helpers/Separator'
import SubmitBar from '../../../../../../packages/kiti/components/form/SubmitBar'
import Text, { Title } from '../../../../../../packages/kiti/components/presentation/Text'
import Textarea from '../../../../../../packages/kiti/components/form/Textarea'
import UsersOrGroupsSelect from '../../../../../../containers/users/select/UsersOrGroupsSelect'
import { getGetSingleConditionValue } from '../../../_helpers/singleConditionValue'
import { useInterpolations } from '../../../../../../utils/interpolations/interpolations'
import { useRawInterpolations } from '../../../../../../utils/interpolations/rawInterpolations'
import InputWithInterpolations from '../../../../../../packages/kiti-editor/components/input/InputWithInterpolations'
import DateInputWithInterpolations from '../../../../../../packages/kiti-editor/components/date/DateInputWithInterpolations'
import TypeSelect from '../insertServiceOrder/TypeSelect'
import { useShouldHideTypeSelect, useShouldHideBondSelect } from '../../_helpers/shouldHideField'
import Select from '../../../../../../packages/kiti/components/form/Select'
import { usePlaceTypesWithPermission } from '../../../../../../utils/helpers/placeTypes'
import NumberInput from '../../../../../../components/form/NumberInput'

const InsertServiceOrdersSentence = ({ isFuture = false, schedulerTask = null }) => {
  const canUseName = useSessionPermission('editServiceOrdersName')
  const [open, setOpen] = React.useState()
  const { t } = useTranslation('triggers')
  const { form, handleChangeIn } = useFormState()
  const getComplementaryOptions = useComplementaryOptionsGenerator()
  const placeTypes = usePlaceTypesWithPermission('list')

  const size = Array.isArray(form?.payload) && form?.payload?.filter(path(['checklist', 'id'])).length

  const HOURS = Array.from({ length: 23 }, (_, i) => String(i + 1).padStart(2, '0'))
  const MINUTES = Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, '0'))

  const checklistId = getGetSingleConditionValue(form.conditions, 'checklist_id')
  const interpolations = useInterpolations(form.entity, checklistId)
  const rawInterpolations = useRawInterpolations(form.entity, checklistId)

  const shouldHideBondSelect = useShouldHideBondSelect()
  const shouldHideTypeSelect = useShouldHideTypeSelect()

  const hasInterpolations = interpolations.length > 0 || rawInterpolations.length > 0

  const handleAfterChangePlaceType = (_field, { path }) => {
    handleChangeIn([...path, 'place'], undefined)
  }

  React.useEffect(() => {
    if (!!schedulerTask) handleChangeIn(['configuration', 'entity'], 'scheduler_task')
  }, schedulerTask)

  React.useEffect(() => {
    if (!form?.configuration?.hours) {
      handleChangeIn(['configuration', 'hours'], '01')
    }

    if (!form?.configuration?.days) {
      handleChangeIn(['configuration', 'days'], '0')
    }

    if (!form?.configuration?.minutes) {
      handleChangeIn(['configuration', 'minutes'], '00')
    }
  }, [])

  return (
    <Grid align="center" gap={1} nowrap>
      {!!isFuture && (
        <>
          <Text size="h2">após</Text>
          <EditableSentence
            label={form?.configuration?.days || '___'}
            field="value"
            Input={NumberInput}
            value={form?.configuration?.days}
            path={['configuration', 'days']}
            onChange={event => {
              if (event < 0 || !event || event == '') {
                handleChangeIn(['configuration', 'days'], '0')
              }

              handleChangeIn(['configuration', 'days'], event)
            }}
            onlyInt
            min={0}
          />
          <Text size="h2">{form?.configuration?.days > 1 ? t('day_plural') : t('day')}, </Text>
          <EditableSentence
            label={form?.configuration?.hours || '01'}
            field="time"
            value={form?.configuration?.hours}
            path={['configuration', 'hours']}
            onChange={event => {
              if (event < 1 || !event || event == '') {
                handleChangeIn(['configuration', 'hours'], '01')
              }

              handleChangeIn(['configuration', 'hours'], event)
            }}
            options={HOURS}
            truncate={15}
            width={10}
          />
          <Text size="h2">{form?.configuration?.hours > 1 ? t('hour_plural') : t('hour')} e</Text>
          <EditableSentence
            label={form?.configuration?.minutes || '___'}
            field="value"
            value={form?.configuration?.minutes}
            path={['configuration', 'minutes']}
            onChange={event => {
              if (event < 0 || !event || event == '') {
                handleChangeIn(['configuration', 'minutes'], '00')
              }

              handleChangeIn(['configuration', 'minutes'], event)
            }}
            options={MINUTES}
            truncate={15}
            width={10}
          />
          <Text size="h2">{form?.configuration?.minutes > 1 ? t('minute_plural') : t('minute')}</Text>
        </>
      )}
      <Text size="h2">: </Text>
      <EditableSentence
        label={size ? t('serviceOrdersCount', { count: size }) : '_______'}
        truncate={20}
        onClick={() => setOpen(true)}
        field="payload"
      />

      <Modal open={open} onClose={() => setOpen(false)} fullScreen noPadding>
        <Separator paddingV={1.7} paddingH={2}>
          <Grid justify="space-between">
            <Col flex={1}>
              <Title size="h5" noMargin>
                {t('bulkRegistration')}
              </Title>
            </Col>
            <Col>
              <Icon name="BsX" onClick={() => setOpen(false)} size={1.5} color="dark1" />
            </Col>
          </Grid>
        </Separator>
        <Line />

        <Scroll height="69vh" scroll scrollX direction="row">
          <Separator weight={8} flex={1}>
            <BulkForm
              hasInterpolations={hasInterpolations}
              path={['payload']}
              fields={[
                ...appendIf(canUseName, {
                  label: t('fields.name'),
                  name: 'name',
                  width: 18,
                  Input: InputWithInterpolations,
                  dynamicProps: () => ({ interpolations })
                }),
                {
                  label: t('fields.checklist'),
                  name: 'checklist',
                  Input: ChecklistSelect,
                  dynamicProps: () => ({ interpolations: rawInterpolations }),
                  mergedOptions: getComplementaryOptions('checklist')
                },
                {
                  label: t('fields.destinationType'),
                  name: 'placeType',
                  Input: Select,
                  options: placeTypes.map(type => ({ id: type.id, value: type.key, name: type.namePlural })),
                  afterChange: handleAfterChangePlaceType
                },
                {
                  label: t('fields.destination'),
                  name: 'place',
                  Input: PlaceSelect,
                  dynamicProps: item => ({ type: item?.placeType?.value, interpolations: rawInterpolations }),
                  mergedOptions: getComplementaryOptions('place')
                },

                {
                  label: t('fields.asset'),
                  name: 'asset',
                  Input: AssetSelect,
                  dynamicProps: item => ({
                    filter: { placeIds: item?.place?.id },
                    disabled: !item?.place?.id,
                    interpolations: rawInterpolations
                  }),
                  mergedOptions: getComplementaryOptions('asset')
                },

                {
                  label: t('fields.responsible'),
                  name: 'user_or_group',
                  Input: UsersOrGroupsSelect,
                  dynamicProps: () => ({ interpolations: rawInterpolations }),
                  width: 22,
                  mergedOptions: getComplementaryOptions('user')
                },
                {
                  label: t('fields.scheduling'),
                  name: 'scheduling',
                  Input: DateInputWithInterpolations,
                  dynamicProps: () => ({ interpolations: rawInterpolations }),
                  useTime: true
                },
                ...appendIf(!shouldHideBondSelect(), {
                  label: t('fields.bond'),
                  name: 'bond',
                  Input: BondSelect,
                  width: 16
                }),
                ...appendIf(!shouldHideTypeSelect(), {
                  label: t('fields.type'),
                  name: 'typeSelect',
                  Input: TypeSelect,
                  width: 16
                }),

                {
                  label: t('fields.description'),
                  name: 'description',
                  Input: InputWithInterpolations,
                  dynamicProps: () => ({ interpolations }),
                  width: 24
                },
                { label: t('fields.metadata'), name: 'metadata', Input: Textarea, width: 24 }
              ]}
            />
          </Separator>
        </Scroll>
        <Line />

        <Separator paddingH={2}>
          <SubmitBar onClick={() => setOpen(false)} />
        </Separator>
      </Modal>
    </Grid>
  )
}

export default InsertServiceOrdersSentence
